import { PRE_HEARING_CASE_REVIEW_TERMINOLOGY } from "./pre-hearing-case-review.terminology";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { createTerminologyGridHeader } from "@app/shared/terminology/terminology-grid-header";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { TerminologyFormIdentityDirective } from "@app/shared/terminology/terminology-form-identity.directive";
import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, Signal, computed } from "@angular/core";
import { Validators } from "@angular/forms";
import { EzFormInputType, InputChangeEvent, InputConfig } from "@ez-library/types/ez-form.types";
import { ColDef, GridOptions } from "ag-grid-community";
import { UiLibraryModule } from "@template-ui/template-ui.module";

import {
  HearingRecordDto,
  HearingRecordOffense,
  HearingRecordPriorHearing,
} from "../hearing-record.models";

@Component({
  selector: "phms-pre-hearing-case-review-section",
  standalone: true,
  imports: [CommonModule, UiLibraryModule, TerminologyIdentityDirective, TerminologyFormIdentityDirective],
  templateUrl: "./pre-hearing-case-review-section.component.html",
  styleUrl: "./pre-hearing-case-review-section.component.scss",
})
export class PreHearingCaseReviewSectionComponent {
  @Input({ required: true }) record!: Signal<HearingRecordDto | null>;
  @Input({ required: true }) canEdit!: Signal<boolean>;
  @Input() riskReviewRequired = false;
  @Input() releasePlanConfig: InputConfig[] = [];
  @Input() riskValues: Record<string, unknown> = {};

  @Output() inputChange = new EventEmitter<InputChangeEvent<unknown>>();
  @Output() riskValuesChange = new EventEmitter<Record<string, unknown>>();

  readonly terminologyUsage = PRE_HEARING_CASE_REVIEW_TERMINOLOGY;
  readonly text = createTerminologyUsageSignals(this.terminologyUsage);
  readonly behaviorFormUsage = { institutionalBehaviorSummary: this.terminologyUsage.behaviorSummary };
  private readonly hearingDateHeader = createTerminologyGridHeader(this.terminologyUsage.hearingDate, this.text.hearingDate);
  private readonly hearingTypeHeader = createTerminologyGridHeader(this.terminologyUsage.hearingType, this.text.hearingType);
  private readonly hearingPanelHeader = createTerminologyGridHeader(this.terminologyUsage.hearingPanel, this.text.hearingPanel);
  private readonly outcomeHeader = createTerminologyGridHeader(this.terminologyUsage.outcome, this.text.outcome);
  private readonly statusHeader = createTerminologyGridHeader(this.terminologyUsage.status, this.text.status);

  readonly offenses = computed(() => this.record()?.context.offenses ?? []);
  readonly controllingOffense = computed(() => findControllingOffense(this.offenses()));
  readonly priorHearings = computed(() => this.record()?.context.priorHearings ?? []);
  readonly violations = computed(() => filterHistory(this.offenses(), "violation"));
  readonly programming = computed(() => filterHistory(this.offenses(), "programming"));
  readonly incarcerationSummary = computed(() =>
    this.offenses().find((offense) => normalize(offense.recordCategoryCode) === "incarceration_history")?.summary?.trim()
      || "Unavailable",
  );
  readonly ageAtOffense = computed(() => this.record()?.derived?.ageAtOffense ?? null);
  readonly controllingOffenseRows = computed(() => this.controllingOffense() ? [{
    offense: this.offenseLabel(this.controllingOffense()),
    statute: this.controllingOffense()?.statuteReference || "—",
    offenseDate: this.formatDate(this.controllingOffense()?.eventDate),
    sentence: this.incarcerationSummary(),
    ageAtOffense: this.ageAtOffense() ?? "—",
    disposition: this.controllingOffense()?.dispositionCode || "—",
  }] : []);
  readonly riskConfig: InputConfig[] = [{
    name: "riskCraNote", type: EzFormInputType.TEXTAREA, label: "Risk assessment review *",
    textAreaRows: 5, row: 1, width: "fill", floatLabel: "always",
    validation: [Validators.required, Validators.pattern(/\S/)],
    validationMessages: { required: "Risk assessment review is required.", pattern: "Enter a risk assessment review." },
  }];
  readonly behaviorConfig = computed<InputConfig[]>(() => [{
    name: "institutionalBehaviorSummary", type: EzFormInputType.TEXTAREA,
    label: this.text.behaviorSummary(), textAreaRows: 5, row: 1, width: "fill", floatLabel: "always",
  }]);
  readonly historyColumnDefaults: ColDef = {
    flex: 1, minWidth: 95, editable: false, sortable: false, filter: false,
    resizable: true, wrapText: true, autoHeight: true, wrapHeaderText: true, autoHeaderHeight: true,
  };
  readonly historyGridOptions: GridOptions = {
    pagination: false, defaultColDef: this.historyColumnDefaults,
  };
  readonly controllingOffenseColumns: ColDef[] = [
    { field: "offense", headerName: "Controlling offense", flex: 2, minWidth: 180 },
    { field: "statute", headerName: "Statute" },
    { field: "offenseDate", headerName: "Offense date", minWidth: 120 },
    { field: "sentence", headerName: "Sentence / custody term", flex: 2, minWidth: 180 },
    { field: "ageAtOffense", headerName: "Age at offense" },
    { field: "disposition", headerName: "Disposition" },
  ];
  readonly priorHearingColumns = computed<ColDef<HearingRecordPriorHearing>[]>(() => [
    { field: "hearingDate", ...this.hearingDateHeader(), valueFormatter: ({ value }) => this.formatDate(value) },
    { field: "hearingType", ...this.hearingTypeHeader(), valueFormatter: ({ data }) => data ? this.hearingLabel(data) : "Hearing" },
    { field: "decisionCode", ...this.outcomeHeader() },
    { field: "panelSummary", ...this.hearingPanelHeader(), minWidth: 150 },
    { field: "notes", headerName: "Notes", minWidth: 180, flex: 2 },
  ]);
  readonly violationColumns = computed<ColDef<HearingRecordOffense>[]>(() => [
    { field: "eventDate", headerName: "Date", valueFormatter: ({ value }) => this.formatDate(value) },
    { field: "summary", headerName: "Description", flex: 2, minWidth: 180 },
    { field: "dispositionCode", ...this.statusHeader() },
  ]);
  readonly programmingColumns: ColDef<HearingRecordOffense>[] = [
    { field: "eventDate", headerName: "Date", valueFormatter: ({ value }) => this.formatDate(value) },
    { field: "summary", headerName: "Program / activity", flex: 3, minWidth: 180 },
  ];

  onInputChange(event: InputChangeEvent<unknown>): void {
    if (!this.canEdit()) return;
    this.onRiskValuesChange({ [event.name]: event.value });
    this.inputChange.emit(event);
  }

  onRiskValuesChange(values: Record<string, unknown>): void {
    if (!this.canEdit()) return;
    this.riskValues = { ...this.riskValues, ...values };
    this.riskValuesChange.emit(this.riskValues);
  }

  formatDate(value: string | null | undefined): string {
    const date = value ? new Date(value) : null;
    return !date || Number.isNaN(date.getTime())
      ? "—"
      : new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(date);
  }

  offenseLabel(offense: HearingRecordOffense | null): string {
    return offense?.summary?.trim().replace(/^controlling offense:\s*/i, "") || "Unavailable";
  }

  hearingLabel(hearing: HearingRecordPriorHearing): string {
    return hearing.hearingType?.trim() || "Hearing";
  }
}

function findControllingOffense(offenses: HearingRecordOffense[]): HearingRecordOffense | null {
  return offenses.find((offense) => normalize(offense.summary).includes("controlling"))
    ?? offenses.find((offense) => normalize(offense.recordCategoryCode) === "offense")
    ?? offenses[0]
    ?? null;
}

function filterHistory(offenses: HearingRecordOffense[], category: string): HearingRecordOffense[] {
  return offenses.filter((offense) => normalize(offense.recordCategoryCode) === category);
}

function normalize(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}
